import Resizer from 'react-image-file-resizer';

export const getFileSizeString = (fileSize: number) => {
  if (fileSize < 1024) {
    return `${fileSize} bytes`;
  } else if (fileSize > 1024 && fileSize < 1048576) {
    return (fileSize / 1024).toFixed(1) + 'KB';
  } else if (fileSize > 1048576) {
    return (fileSize / 1048576).toFixed(1) + 'MB';
  }
  return '';
};

export const isInvalidImageFileType = (file: File | undefined | null) => {
  if (!file) {
    return true;
  }
  const validImageTypes = [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
  ];
  return validImageTypes.filter((type) => type === file.type).length === 0;
};

export const MAX_FILE_SIZE = 1024 * 1024 * 30;

export const isFileDuplicated = (newFile: File, existingFiles: File[]) => {
  return existingFiles.filter((file) => file.name === newFile.name).length > 0;
};

export const validateFile = (
  newFile: File,
  maxFileSize: number = MAX_FILE_SIZE,
) => {
  if (newFile) {
    if (newFile.size >= maxFileSize) {
      throw new Error(
        `The file you have uploaded is ${getFileSizeString(newFile.size)}, the file must be less than ${getFileSizeString(maxFileSize)}`,
      );
    } else if (isInvalidImageFileType(newFile)) {
      throw new Error(
        `The file you have uploaded is not a valid image file type, we only accept JPEG, PNG, GIF, WebP file types.`,
      );
    }
    return true;
  } else {
    throw new Error('No file is uploaded');
  }
};

export const resizeFile = async (file: File): Promise<File> => {
  const fileType = file.type.split('/')[1].toUpperCase();
  const dataURL = await new Promise((resolve) => {
    Resizer.imageFileResizer(
      file,
      300,
      300,
      fileType,
      100,
      0,
      (uri) => {
        resolve(uri);
      },
      'base64',
    );
  });
  const blob = await fetch(dataURL as string).then((res) => res.blob());
  return new File([blob], file.name, {
    type: file.type,
  });
};

export const disableDropzone = (
  maxNumberOfUploads: number,
  uploadedFiles: File[] | null,
) => {
  if (maxNumberOfUploads >= 1) {
    if (
      (uploadedFiles && uploadedFiles.length < maxNumberOfUploads) ||
      !uploadedFiles
    ) {
      return false;
    }
  }
  return true;
};

/**
 * this function is used to detect if the file is a newly taken photo
 */
export const isPhotoNewlyTaken = (file: File) => {
  // check if the file was created in the last 2 minutes
  return file.lastModified > Date.now() - 120000;
};

/**
 * this function is used to detect if the file is a duplicate and rename it if it is
 * @param newFile the new file that is being uploaded
 * @param existingFiles the existing files that are already uploaded
 * @returns an array of files with the new file added
 */

export const detectDuplicateFilesAndRename = (
  newFile: File,
  existingFiles: File[],
) => {
  const newFileName = newFile.name;
  const nameWithoutExtension = newFileName.slice(
    0,
    newFileName.lastIndexOf('.'),
  );
  const extension = newFileName.slice(newFileName.lastIndexOf('.'));

  let nameConflict = false;

  for (let file of existingFiles) {
    if (file.name === newFileName) {
      // if the file is not newly taken, then it is a genuine duplicate, throw an error
      if (!isPhotoNewlyTaken(newFile)) {
        throw new Error('The file that you have uploaded is a duplicate');
      } else {
        // if the file is newly taken, but it has the same name (due to taken and uploaded from mobile), then rename it
        // this is because if the file is taken and uploaded from mobile directly, the default file name is "image.jpg"
        nameConflict = true;
        break;
      }
    }
  }

  if (nameConflict) {
    let counter = 2;
    let newName: string;
    /**
     *  loop until the counter arrives at a number that does not exist in the existing files
     *  for eg: image_2.jpg, image_3.jpg, image_4.jpg
     */
    do {
      newName = `${nameWithoutExtension}_${counter}${extension}`;
      counter++;
    } while (existingFiles.some((file) => file.name === newName));

    newFile = new File([newFile], newName, {
      type: newFile.type,
      lastModified: newFile.lastModified,
    });
  }

  return [...existingFiles, newFile];
};
